import { 
    Box, 
    Button, 
    CircularProgress, 
    IconButton, 
    InputAdornment, 
    Stack, 
    TextField, 
    ToggleButton, 
    ToggleButtonGroup, 
    Typography,
    Paper
} from "@mui/material";
import renderAvatar from "../../../../components/render-avatar";
import { memo, useEffect, useState } from "react";
import { Classroom } from "../../../../models/classroom";
import { Delete, Upload } from "@mui/icons-material";
import EnrollmentTypeChangeDialog from "../components/enrollment-type-change-dialog";
import RichTextEditor from "../../components/rich-text/rich-text-editor";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";

interface ClassroomDetailsProps {
    cls: Classroom;
}

function ClassroomDetails({ cls }: ClassroomDetailsProps) {
    const [classroom, setClassroom] = useState(cls);
    
    const [loading, setLoading] = useState(false);
    const [enrollmentChangeDialog, setEnrollmentChangeDialog] = useState(-1);

    const { updateClassroom } = useClassroomActions();

    const calculateStudentCharges = (): number => {
        const ins_amm = Number(classroom.instructor_amount) || 0;
        return +(Math.max(100 + ins_amm, ins_amm + (ins_amm * 0.05))).toFixed(2);
    };

    const calculatePlatformFee = (): number => {
        const ins_amm = Number(classroom.instructor_amount) || 0;
        return +(calculateStudentCharges() - ins_amm).toFixed(2);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.[0]) {
            if (classroom.image) classroom.removed_image = classroom.image;
            handleClassroomChange("image_file", event.target.files[0]);
        }
    };
    
    const handleClassroomChange = (field: keyof Classroom, value: any) => {
        setClassroom((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const preUpdateClassroom = () => {
        if (cls.enrollment_type !== classroom.enrollment_type) {
            setEnrollmentChangeDialog(classroom.enrollment_type);
        } else {
            updateClassroomDetails();
        }
    };

    const updateClassroomDetails = async () => {    
        setLoading(true);
        await updateClassroom(classroom);
        setLoading(false);
    };

    // console.log(classroom);
    
    useEffect(() => {
        setClassroom(cls);
    }, [cls]);

    return (
        <Stack spacing={3}>

            <Box
                sx={{
                    borderRadius: 2,
                    p: 1,
                    border: '1px solid #e5e7eb',
                    backgroundColor: "grey.50",
                }}
            >            
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: "row", md: "column" }, justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        {renderAvatar(
                            classroom.id,
                            classroom.name,
                            classroom.image_file ? classroom.image_file : classroom.image,
                            100,
                            100
                        )}
                        
                        <Stack direction="row" spacing={1}>
                            <IconButton
                                size="small"
                                component="label"
                                sx={{ 
                                    backgroundColor: '#f3f4f6', 
                                    '&:hover': { backgroundColor: '#e5e7eb' }
                                }}
                            >
                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                <Upload fontSize="small" />
                            </IconButton>

                            <IconButton
                                size="small"
                                sx={{ 
                                    backgroundColor: '#f3f4f6', 
                                    '&:hover': { backgroundColor: '#fee2e2', color: '#dc2626' }
                                }}
                                onClick={() => {
                                    handleClassroomChange('removed_image', classroom.image);
                                    handleClassroomChange('image', '');
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Stack spacing={2.5} flex={1}>
                        <TextField
                            fullWidth
                            label="Classroom Name"
                            value={classroom.name}
                            onChange={(e) => handleClassroomChange('name', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'white',
                                }
                            }}
                        />

                        <RichTextEditor
                            id={String(classroom.id)}
                            value={classroom.description}
                            onChange={(val) => handleClassroomChange('description', val)}
                            type="description"
                            userPlaceholder="Update Notice Board"
                        />
                    </Stack>
                </Stack>
            </Box>

            {/* Pricing Section */}
            <Box
                sx={{
                    backgroundColor: "grey.50",
                    borderRadius: 2,
                    p: 1,
                    border: '1px solid #e5e7eb'
                }}
            >
                
                <Stack spacing={3}>

                    <Typography 
                        variant="subtitle1" 
                        sx={{ 
                            fontWeight: 600,
                            color: 'text.primary',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        Pricing
                    </Typography>
                    
                    <TextField
                        type="number"
                        label="Your Enrollment Fee"
                        value={classroom.instructor_amount}
                        onChange={(e) => handleClassroomChange('instructor_amount', e.target.value)}
                        InputProps={{ 
                            startAdornment: <InputAdornment position="start">₦</InputAdornment> 
                        }}
                        sx={{ 
                            maxWidth: 300,
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'white',
                            }
                        }}
                    />

                    <Box
                        sx={{
                            bgcolor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: 2,
                            p: 2.5,
                        }}
                    >
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={3}
                            divider={<Box sx={{ width: { xs: '100%', sm: '1px' }, height: { xs: '1px', sm: 'auto' }, bgcolor: '#e5e7eb' }} />}
                        >
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant="body2" color="#6b7280" fontWeight={500} sx={{ mb: 0.5 }}>
                                    Students Pay
                                </Typography>
                                <Typography variant="h6" fontWeight={600} color="#111827">
                                    ₦{calculateStudentCharges()}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant="body2" color="#6b7280" fontWeight={500} sx={{ mb: 0.5 }}>
                                    Platform Fee
                                </Typography>
                                <Typography variant="h6" fontWeight={600} color="#dc2626">
                                    ₦{calculatePlatformFee()}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant="body2" color="#6b7280" fontWeight={500} sx={{ mb: 0.5 }}>
                                    You Receive
                                </Typography>
                                <Typography variant="h6" fontWeight={600} color="#059669">
                                    ₦{classroom.instructor_amount || 0}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
            </Box>

            {classroom.instructor_amount > 0 && (
                <Box
                    sx={{
                        borderRadius: 2,
                        p: 1,
                        border: '1px solid #e5e7eb',
                        backgroundColor: "grey.50",
                    }}
                >
                    
                    <Stack spacing={3}>
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 3
                            }}
                        >
                            Enrollment Type
                        </Typography>

                        <ToggleButtonGroup
                            value={classroom.enrollment_type}
                            exclusive
                            onChange={(_, value) => {
                                if (value !== null) handleClassroomChange('enrollment_type', Number(value));
                            }}
                            sx={{
                                bgcolor: '#f3f4f6',
                                borderRadius: 2,
                                p: 0.5,
                                border: 'none',
                                alignSelf: 'flex-start',
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: 'none',
                                    borderRadius: '8px !important',
                                    mx: 0.25,
                                },
                                '& .MuiToggleButton-root': {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    color: '#6b7280',
                                    border: 'none',
                                    px: 3,
                                    py: 1,
                                    '&:hover': {
                                        backgroundColor: '#e5e7eb',
                                    },
                                },
                                '& .Mui-selected': {
                                    backgroundColor: '#3b82f6 !important',
                                    color: 'white !important',
                                    '&:hover': {
                                        backgroundColor: '#2563eb !important',
                                    },
                                },
                            }}
                        >
                            <ToggleButton value={0}>One-Time</ToggleButton>
                            <ToggleButton value={1}>Recurring</ToggleButton>
                        </ToggleButtonGroup>

                        {classroom.enrollment_type === 1 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    bgcolor: '#fef3c7',
                                    borderRadius: 2,
                                    border: '1px solid #fcd34d',
                                }}
                            >
                                <Typography variant="body2" color="#92400e" fontWeight={500}>
                                    Repeat every
                                </Typography>
                                <TextField
                                    type="number"
                                    value={classroom.duration}
                                    onChange={(e) => handleClassroomChange('duration', e.target.value)}
                                    size="small"
                                    sx={{
                                        width: 80,
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                        },
                                        '& input': {
                                            textAlign: 'center',
                                        },
                                    }}
                                    inputProps={{ min: 1 }}
                                />
                                <Typography variant="body2" color="#92400e" fontWeight={500}>
                                    days
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>
            )}

            {/* Save Button */}
            <Button 
                variant="contained"
                size="large"
                sx={{ 
                    alignSelf: 'end',
                    minWidth: 200,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    backgroundColor: '#059669',
                    '&:hover': {
                        backgroundColor: '#047857',
                    }
                }} 
                disabled={!classroom.name || (classroom.enrollment_type === 1 && classroom.duration < 1)}
                onClick={preUpdateClassroom}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"} 
            </Button>

            <EnrollmentTypeChangeDialog
                enrollmentChangeDialog={enrollmentChangeDialog}
                setEnrollmentChangeDialog={setEnrollmentChangeDialog}
                onUpdate={updateClassroomDetails}
            />
        </Stack>
    );
}

export default memo(ClassroomDetails);